import { useEffect, useState } from 'react';
import { ApiError, api, type Category, type NetworkAccounts, type Settings as SettingsData } from './api.js';

type Draft = Pick<Category, 'monthlyQuota' | 'writer' | 'templateSlug' | 'visualTemplateFamilySlug' | 'networks'>;

function draftFor(category: Category, settings: SettingsData): Draft {
  const enabled = new Set(settings.networks.filter((network) => network.enabled).map((network) => network.code));
  return {
    monthlyQuota: category.monthlyQuota,
    writer: category.writer,
    templateSlug: category.templateSlug,
    visualTemplateFamilySlug: category.visualTemplateFamilySlug,
    networks: category.networks.filter((network) => enabled.has(network)),
  };
}

export function Settings({ csrfToken }: { csrfToken: string | undefined }) {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const [accounts, setAccounts] = useState<NetworkAccounts | null>(null);

  useEffect(() => {
    void api.settings().then((result) => {
      setSettings(result);
      setDrafts(Object.fromEntries(result.categories.map((category) => [category.id, draftFor(category, result)])));
    }).catch((reason) => setError(reason instanceof ApiError ? reason.message : 'Les réglages sont indisponibles.'));
    void api.networkAccounts().then(setAccounts).catch(() => setAccounts(null));
  }, []);

  function update(categoryId: string, patch: Partial<Draft>) {
    setDrafts((current) => ({ ...current, [categoryId]: { ...current[categoryId] as Draft, ...patch } }));
  }

  function accountFor(network: 'instagram' | 'facebook' | 'linkedin') {
    return accounts?.accounts.find((account) => account.network === network) ?? null;
  }

  async function save(category: Category) {
    const draft = drafts[category.id];
    if (!draft) return;
    if (draft.networks.length === 0) {
      setError('Choisissez au moins un réseau pour cette catégorie.');
      return;
    }
    setSaving(category.id); setError(null); setMessage(null);
    try {
      await api.updateCategorySettings(category.id, draft, csrfToken);
      setMessage(`Les réglages de « ${category.name} » sont enregistrés.`);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Enregistrement impossible.');
    } finally {
      setSaving(null);
    }
  }

  async function uploadPortrait(file: File) {
    setUploadingPortrait(true); setError(null); setMessage(null);
    try {
      const result = await api.uploadBrandPortrait(file, csrfToken);
      setSettings((current) => current ? { ...current, brandAssets: { ...current.brandAssets, portrait: result.mediaId } } : current);
      setMessage('Le portrait de marque est enregistré. Il sera utilisé dans tous les prochains visuels.');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Chargement du portrait impossible.');
    } finally {
      setUploadingPortrait(false);
    }
  }

  return (
    <section className="bloc settings-page">
      <div className="intro-page">
        <div><p className="surtitre">Pilotage éditorial</p><h2>Une règle claire par catégorie.</h2></div>
        <p>Choisissez où publier, quel gabarit utiliser et combien de contenus préparer. Les modifications s’appliquent aux prochains sujets.</p>
      </div>

      {error ? <p className="erreur" role="alert">{error}</p> : null}
      {message ? <p className="succes" role="status">{message}</p> : null}
      {!settings ? <p className="sous-titre">Chargement…</p> : null}

      {settings ? <section className="settings-brand">
        <div className="settings-brand-preview">{settings.brandAssets.portrait ? <img src={`/api/media/${settings.brandAssets.portrait}`} alt="Portrait de marque actuel" /> : <span>AD</span>}</div>
        <div><p className="surtitre">Identité visuelle</p><h3>Portrait d’Alan</h3><p>Utilisé automatiquement dans les emplacements agent, avatar et portrait des gabarits.</p></div>
        <label className="action-secondaire"><input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploadingPortrait} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void uploadPortrait(file); }} />{uploadingPortrait ? 'Chargement…' : settings.brandAssets.portrait ? 'Remplacer le portrait' : 'Ajouter le portrait'}</label>
      </section> : null}

      <section className="settings-connections">
        <div><p className="surtitre">Diffusion</p><h3>Comptes sociaux</h3><p>La connexion est chiffrée et indispensable avant toute publication automatique.</p></div>
        <div className="connection-grid">
          <ConnectionCard label="Facebook & Instagram" account={accountFor('facebook') ?? accountFor('instagram')} available={accounts?.available.meta ?? false} onConnect={() => window.location.assign('/api/auth/meta/connect')} />
          <ConnectionCard label="LinkedIn" account={accountFor('linkedin')} available={accounts?.available.linkedin ?? false} onConnect={() => window.location.assign('/api/auth/linkedin/connect')} />
          <div className="connection-card pending"><strong>Google Business Profile</strong><p>Connexion à venir. Social prépare déjà le PNG carré compatible.</p></div>
        </div>
      </section>

      {settings ? <div className="settings-grid">
        {settings.categories.map((category) => {
          const draft = drafts[category.id] ?? draftFor(category, settings);
          return <article className="settings-card" key={category.id}>
            <header><div><span className="surtitre">{category.monthlyQuota === 0 ? 'À la demande' : `Maximum ${category.monthlyQuota} par mois`}</span><h3>{category.name}</h3></div><span className="settings-objective">Objectif {category.objectiveKpi}</span></header>
            <p>{category.description}</p>

            <div className="settings-fields">
              <label>Gabarit visuel
                <select value={draft.visualTemplateFamilySlug ? `family:${draft.visualTemplateFamilySlug}` : draft.templateSlug ? `template:${draft.templateSlug}` : ''} onChange={(event) => {
                  const [kind, slug] = event.target.value.split(':');
                  update(category.id, kind === 'family' ? { templateSlug: null, visualTemplateFamilySlug: slug || null } : { templateSlug: slug || null, visualTemplateFamilySlug: null });
                }}>
                  <option value="">Aucun gabarit</option>
                  {settings.visualFamilies.map((family) => <option value={`family:${family.slug}`} key={`family:${family.slug}`}>{family.name} · tous formats</option>)}
                  {settings.templates.filter((template) => !template.slug.startsWith('html-')).map((template) => <option value={`template:${template.slug}`} key={template.slug}>{template.name}</option>)}
                </select>
              </label>
              <label>Rédaction
                <select value={draft.writer} onChange={(event) => update(category.id, { writer: event.target.value as Draft['writer'] })}>
                  <option value="gemini">Gemini · économique</option>
                  <option value="anthropic">Claude · premium</option>
                </select>
              </label>
              <label>Plafond mensuel
                <input type="number" min="0" max="31" value={draft.monthlyQuota} onChange={(event) => update(category.id, { monthlyQuota: Number(event.target.value) })} />
                <small>0 signifie : uniquement à la demande. Ce nombre ne force jamais à publier.</small>
              </label>
            </div>

            <fieldset className="settings-networks"><legend>Réseaux</legend>
              {settings.networks.map((network) => <label key={network.code} className={!network.enabled ? 'indisponible' : ''}>
                <input type="checkbox" disabled={!network.enabled} checked={draft.networks.includes(network.code)} onChange={(event) => update(category.id, {
                  networks: event.target.checked ? [...draft.networks, network.code] : draft.networks.filter((code) => code !== network.code),
                })} />
                <span>{network.label}</span>{!network.enabled ? <small>Bientôt</small> : null}
              </label>)}
            </fieldset>
            <button type="button" disabled={saving !== null} onClick={() => void save(category)}>{saving === category.id ? 'Enregistrement…' : 'Enregistrer cette catégorie'}</button>
          </article>;
        })}
      </div> : null}
    </section>
  );
}

function ConnectionCard({ label, account, available, onConnect }: { label: string; account: NetworkAccounts['accounts'][number] | null; available: boolean; onConnect: () => void }) {
  const status = account?.status === 'active' ? 'Connecté' : account?.status === 'expiring' ? 'À reconnecter' : 'Non connecté';
  return <div className="connection-card"><div><strong>{label}</strong><span className={account?.status === 'active' ? 'connected' : 'disconnected'}>{status}</span></div><p>{account?.displayName ?? (available ? 'Prêt à être connecté.' : 'Identifiants applicatifs à configurer dans Coolify.')}</p>{account?.lastError ? <small>{account.lastError}</small> : null}<button type="button" disabled={!available} onClick={onConnect}>{account?.status === 'active' ? 'Reconnecter' : 'Connecter'}</button></div>;
}
